import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { LogoUpload } from '@/components/ui/logo-upload';
import { PhoneInput } from '@/components/ui/phone-input';
import { PageHeader } from '@/components/dashboard/shared/PageHeader';
import { Loader2 } from 'lucide-react';

import { isValidPhoneNumber } from 'react-phone-number-input';

type Address = {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
};

type BulkRow = {
  rowIndex: number;
  email: string;
  userName: string;
  phoneNumber: string;
  businessName: string;
  businessType: string;
  storeName: string;
  logoUrl: string;
  address: Address;
  storeUrl: string | null;
  status: 'valid' | 'invalid';
  errors: string[];
};

function getApiBaseUrl() {
  return import.meta.env.VITE_API_URL || 'http://localhost:5000';
}

export function OnboardingAdminPage() {
  const navigate = useNavigate();

  const [tab, setTab] = useState<'single' | 'bulk'>('single');

  // Single onboarding state
  const [single, setSingle] = useState({
    email: '',
    userName: '',
    phoneNumber: '',
    businessName: '',
    businessType: 'other',
    logoBase64: '',
    logoUrl: '',
    address: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'Nigeria',
    } as Address,
  });
  const [singleSubmitting, setSingleSubmitting] = useState(false);

  // Bulk onboarding state
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [bulkPreview, setBulkPreview] = useState<BulkRow[]>([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [selectedRowIndexes, setSelectedRowIndexes] = useState<Set<number>>(new Set());

  const validSelectedRows = useMemo(() => {
    const selected = new Set(selectedRowIndexes);
    return bulkPreview.filter((r) => r.status === 'valid' && selected.has(r.rowIndex));
  }, [bulkPreview, selectedRowIndexes]);

  const storeLinkBase = (storeUrl: string) => `/${storeUrl}`;

  const uploadBulkPreview = async () => {
    if (!bulkFile) {
      toast.error('Choose a CSV file first');
      return;
    }

    setBulkLoading(true);
    try {
      const token = localStorage.getItem('sq_token') || '';
      const formData = new FormData();
      // Backend expects field name "file"
      formData.append('file', bulkFile);

      const res = await fetch(`${getApiBaseUrl()}/api/admin/onboarding/bulk/preview`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: formData,
      });

      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload?.message || 'Bulk preview failed');

      const rows = payload?.rows as BulkRow[] | undefined;
      if (!rows) throw new Error('Invalid preview response');

      setBulkPreview(rows);
      setSelectedRowIndexes(
        new Set(rows.filter((r) => r.status === 'valid').map((r) => r.rowIndex)),
      );
      toast.success(`Parsed ${rows.length} rows`);
    } catch (e: any) {
      toast.error(e?.message || 'Bulk preview failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const commitBulk = async () => {
    if (validSelectedRows.length === 0) {
      toast.error('Select at least one valid row');
      return;
    }

    setBulkSubmitting(true);
    try {
      const token = localStorage.getItem('sq_token') || '';

      const res = await fetch(`${getApiBaseUrl()}/api/admin/onboarding/bulk/commit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({
          rows: validSelectedRows,
        }),
      });

      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload?.message || 'Bulk onboarding failed');

      const results = payload?.results as Array<any>;
      const successCount = results?.filter((r) => r.status === 'success').length || 0;
      const failedCount = results?.filter((r) => r.status === 'failed').length || 0;

      toast.success(`Onboarding completed. Success: ${successCount}, Failed: ${failedCount}`);

      // Reset selection after commit so admins get a fresh preview.
      setSelectedRowIndexes(new Set());
    } catch (e: any) {
      toast.error(e?.message || 'Bulk onboarding failed');
    } finally {
      setBulkSubmitting(false);
    }
  };

  const submitSingle = async () => {
    setSingleSubmitting(true);
    try {
      if (!single.email.trim()) throw new Error('Email is required');
      if (!single.userName.trim()) throw new Error('User name is required');
      if (!single.businessName.trim()) throw new Error('Business name is required');
      if (!single.phoneNumber.trim()) throw new Error('Phone number is required');
      if (!isValidPhoneNumber(single.phoneNumber)) throw new Error('Please enter a valid phone number');

      const addr = single.address;
      if (!addr.street || !addr.city || !addr.state || !addr.postalCode || !addr.country) {
        throw new Error('Please complete all address fields');
      }

      if (!single.logoBase64 && !single.logoUrl) throw new Error('Logo is required');

      const token = localStorage.getItem('sq_token') || '';

      const res = await api.post('/api/admin/onboarding/single', {
        email: single.email,
        userName: single.userName,
        phoneNumber: single.phoneNumber,
        businessName: single.businessName,
        businessType: single.businessType,
        logoUrl: single.logoUrl || undefined,
        logoBase64: single.logoBase64 || undefined,
        address: single.address,
      }, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      });

      const storeUrl = res.data?.store?.url as string | undefined;
      toast.success('User and store onboarded successfully');

      if (storeUrl) {
        // Navigate to the store in a new tab.
        window.open(storeLinkBase(storeUrl), '_blank', 'noopener,noreferrer');
      }

      navigate('/admin/stores');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Single onboarding failed');
    } finally {
      setSingleSubmitting(false);
    }
  };

  const addressInput = (key: keyof Address, label: string, required = true) => {
    return (
      <div className="space-y-2">
        <Label htmlFor={`addr-${key}`}>{label}</Label>
        <Input
          id={`addr-${key}`}
          value={single.address[key]}
          onChange={(ev) => setSingle((prev) => ({ ...prev, address: { ...prev.address, [key]: ev.target.value } }))}
          required={required}
        />
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Onboarding"
        subtitle="Onboard platform users and their stores (single or bulk CSV)."
      />

      <div className="flex gap-3">
        <Button type="button" variant={tab === 'single' ? 'default' : 'outline'} onClick={() => setTab('single')}>
          Single onboarding
        </Button>
        <Button type="button" variant={tab === 'bulk' ? 'default' : 'outline'} onClick={() => setTab('bulk')}>
          Bulk CSV onboarding
        </Button>
      </div>

      {tab === 'single' && (
        <Card>
          <CardHeader>
            <CardTitle>Single onboarding</CardTitle>
            <CardDescription>Creates the owner user + store immediately and emails a welcome message.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={single.email} onChange={(e) => setSingle((p) => ({ ...p, email: e.target.value }))} />
              </div>

              <div className="space-y-2">
                <Label>User name</Label>
                <Input value={single.userName} onChange={(e) => setSingle((p) => ({ ...p, userName: e.target.value }))} />
              </div>

              <div className="space-y-2">
                <Label>Phone number</Label>
                <PhoneInput
                  international
                  defaultCountry="NG"
                  value={single.phoneNumber as any}
                  onChange={(v) => setSingle((p) => ({ ...p, phoneNumber: v || '' }))}
                />
              </div>

              <div className="space-y-2">
                <Label>Business name</Label>
                <Input value={single.businessName} onChange={(e) => setSingle((p) => ({ ...p, businessName: e.target.value }))} />
              </div>

              <div className="space-y-2">
                <Label>Business type</Label>
                <Select value={single.businessType} onValueChange={(v) => setSingle((p) => ({ ...p, businessType: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select business type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="other">Other</SelectItem>
                    <SelectItem value="retail">Retail</SelectItem>
                    <SelectItem value="restaurant">Restaurant & Cafe</SelectItem>
                    <SelectItem value="salon">Salon & Beauty</SelectItem>
                    <SelectItem value="services">Services & Booking</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
              <div className="space-y-3">
                <Label>Logo</Label>
                <LogoUpload
                  value={null}
                  onChange={() => {
                    // LogoUpload is base64-only in this admin form.
                  }}
                  initialPreview={undefined}
                  onProcessed={(base64Data) => {
                    setSingle((p) => ({ ...p, logoBase64: base64Data || '' }));
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  LogoUpload compresses and converts your file to base64 before sending it to the server.
                </p>
              </div>

              <div className="space-y-4">
                <Label>Business address</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {addressInput('street', 'Street')}
                  {addressInput('city', 'City')}
                  {addressInput('state', 'State')}
                  {addressInput('postalCode', 'Postal code')}
                  <div className="space-y-2">
                    <Label>Country</Label>
                    <Input
                      value={single.address.country}
                      onChange={(e) => setSingle((p) => ({ ...p, address: { ...p.address, country: e.target.value } }))}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <Button type="button" variant="outline" onClick={() => setTab('bulk')}>
                Upload bulk CSV instead
              </Button>
              <Button type="button" onClick={submitSingle} disabled={singleSubmitting}>
                {singleSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Onboard user + store'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {tab === 'bulk' && (
        <Card>
          <CardHeader>
            <CardTitle>Bulk CSV onboarding</CardTitle>
            <CardDescription>
              Upload a CSV, review results, then onboard the selected valid rows.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-3">
              <Label>CSV file</Label>
              <Input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setBulkFile(e.target.files?.[0] || null)}
              />
              <p className="text-xs text-muted-foreground">
                Required columns: <code>Email</code>, <code>UserName</code>, <code>PhoneNumber</code>, <code>BusinessName</code>, <code>LogoUrl</code>, <code>AddressStreet</code>, <code>AddressCity</code>, <code>AddressState</code>, <code>AddressPostalCode</code>, <code>AddressCountry</code>.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <Button type="button" variant="outline" onClick={() => navigate('/admin/stores')}>
                View stores
              </Button>
              <Button type="button" onClick={uploadBulkPreview} disabled={bulkLoading || !bulkFile}>
                {bulkLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Preview CSV'}
              </Button>
            </div>

            {bulkPreview.length > 0 && (
              <>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setSelectedRowIndexes(new Set(bulkPreview.filter((r) => r.status === 'valid').map((r) => r.rowIndex)));
                    }}
                  >
                    Select all valid
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setSelectedRowIndexes(new Set())}
                  >
                    Clear selection
                  </Button>
                </div>

                <div className="overflow-x-auto overflow-hidden rounded-[1.25rem] border border-[#EBEBEB]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[50px]">Select</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Business</TableHead>
                        <TableHead>Store URL</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Errors</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bulkPreview.map((row) => {
                        const checked = selectedRowIndexes.has(row.rowIndex);
                        return (
                          <TableRow key={row.rowIndex}>
                            <TableCell>
                              <Checkbox
                                checked={checked}
                                disabled={row.status !== 'valid'}
                                onCheckedChange={(v) => {
                                  const next = new Set(selectedRowIndexes);
                                  if (v) next.add(row.rowIndex);
                                  else next.delete(row.rowIndex);
                                  setSelectedRowIndexes(next);
                                }}
                              />
                            </TableCell>
                            <TableCell className="text-sm">{row.email}</TableCell>
                            <TableCell className="text-sm">
                              <div className="font-medium">{row.businessName}</div>
                              <div className="text-xs text-muted-foreground">{row.userName}</div>
                            </TableCell>
                            <TableCell className="text-sm">
                              {row.storeUrl ? (
                                <button
                                  className="text-blue-600 hover:underline"
                                  type="button"
                                  onClick={() => window.open(storeLinkBase(row.storeUrl || ''), '_blank', 'noopener,noreferrer')}
                                >
                                  /{row.storeUrl}
                                </button>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <span className={row.status === 'valid' ? 'text-emerald-700' : 'text-destructive'}>
                                {row.status}
                              </span>
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {row.status === 'invalid' ? row.errors.slice(0, 2).join(' • ') : '—'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                  <div className="text-sm text-muted-foreground">
                    Selected: <span className="font-medium text-foreground">{validSelectedRows.length}</span>
                  </div>
                  <Button type="button" onClick={commitBulk} disabled={bulkSubmitting || validSelectedRows.length === 0}>
                    {bulkSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Onboard selected'}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

